import { useMemo } from "react";
import { useAuth, type useInboxConversation } from "@tellescope/tanstack-query";
import { useInboxThreadSearchParams } from "./use-inbox-thread-search-params";
import { selectThreadDraft } from "../utils";

type InboxConversationData = ReturnType<typeof useInboxConversation>;

export interface UseInboxThreadDraftArgs {
  conversationData: Pick<InboxConversationData, "messages" | "drafts" | "scheduled" | "isLoading">;
}

export const useInboxThreadDraft = ({ conversationData }: UseInboxThreadDraftArgs) => {
  const [queryParams, setQueryParams] = useInboxThreadSearchParams();
  const { currentUser } = useAuth();
  const { messages, drafts, scheduled, isLoading } = conversationData;

  const matchingDraft = useMemo(
    () =>
      drafts.find(({ id }) => id === queryParams.draftId) ??
      scheduled.find(({ id }) => id === queryParams.draftId) ??
      messages.find(({ id }) => id === queryParams.draftId),
    [drafts, messages, queryParams.draftId, scheduled]
  );

  const isEditingScheduled = useMemo(
    () => !!matchingDraft && scheduled.some(({ id }) => id === matchingDraft.id),
    [matchingDraft, scheduled]
  );

  const sendAtInPast = useMemo(
    () => isEditingScheduled && !!matchingDraft?.sendAt && matchingDraft.sendAt.getTime() <= Date.now(),
    [isEditingScheduled, matchingDraft]
  );

  const shouldQueryDraft = !!queryParams.draftId && !matchingDraft && !isLoading;

  const autoSelectedDraft = useMemo(() => selectThreadDraft(drafts, currentUser.id), [drafts, currentUser.id]);

  return {
    queryParams,
    setQueryParams,
    matchingDraft,
    isEditingScheduled,
    sendAtInPast,
    shouldQueryDraft,
    autoSelectedDraft,
  };
};
